import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import Icon from '@components/Icon';
import { Button, Tooltip, Divider, Slider } from 'antd';
import stores from '@stores';
import ColorPicker from '@components/ColorPicker';
import { WidthDropdown } from '@components/header/WidthDropdown';
import { nanoid } from '@utils/utils';
import EmojiSelect from '@components/header/EmojiSelect';
import TextTool from '@components/editor/TextTool';
import StickerTool from '@components/editor/StickerTool';
import { toggleCanvasMove } from '@utils/canvasMove';

// 按类别分组的工具列表
const toolGroups = [
    {
        title: '图形工具',
        icon: <Icon.Square size={18} className="text-purple-500 dark:text-purple-400" />,
        tools: [
            { key: 'Square', name: '矩形' },
            { key: 'SquareFill', name: '填充矩形' },
            { key: 'Circle', name: '圆形' },
            { key: 'Sparkles', name: '五角星' },
            { key: 'Sunset', name: '椭圆' },
            { key: 'Maximize', name: '多边形' }
        ]
    },
    {
        title: '线条工具',
        icon: <Icon.Slash size={18} className="text-purple-500 dark:text-purple-400" />,
        tools: [
            { key: 'Slash', name: '线条' },
            { key: 'MoveDownLeft', name: '箭头' },
            { key: 'Pencil', name: '自由绘制' },
            { key: 'Crop', name: '折线' }
        ]
    },
    {
        title: '样式设置',
        icon: <Icon.Palette size={18} className="text-purple-500 dark:text-purple-400" />,
        custom: 'style'
    },
    {
        title: '标注工具',
        icon: <Icon.MessageCirclePlus size={18} className="text-purple-500 dark:text-purple-400" />,
        tools: [
            { key: 'Magnifier', name: '放大镜' },
            { key: 'Step', name: '序号标记' },
            { key: 'text', name: '文字工具', component: TextTool }
        ]
    },
    {
        title: '装饰工具',
        icon: <Icon.Sparkles size={18} className="text-purple-500 dark:text-purple-400" />,
        tools: [
            { key: 'emoji', name: '表情符号' },
            { key: 'sticker', name: '添加贴纸', component: StickerTool },
            { key: 'move', name: '移动画布' }
        ]
    }
];

export default observer(() => {
    // 添加调试信息，确认组件何时重新渲染
    useEffect(() => {
        console.log("ToolBar组件已渲染/重新渲染，当前移动状态:", stores.editor.move);
        
        return () => {
            console.log("ToolBar组件将卸载");
        };
    }, []);
    
    // 添加全局快捷键监听空格键切换移动模式
    useEffect(() => {
        const handleKeyDown = (e) => {
            // 只在编辑状态且没有输入框聚焦时处理空格键
            if (e.code === 'Space' && stores.editor.isEditing && !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
                e.preventDefault(); // 阻止默认行为
                console.log("空格键被按下，当前移动状态:", stores.editor.move);
                toggleMove();
            }
        };
        
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [stores.editor.move]); // 依赖于stores.editor.move状态，确保获取最新状态
    
    const selectTool = (type) => {
        if (!stores.editor.isEditing) return;
        if (type === 'emoji' || type === 'move' || type === 'sticker') return;
        
        const { useTool } = stores.editor;
        stores.editor.setUseTool(useTool === type ? null : type);
        
        // 切换其他工具时关闭移动模式
        if (stores.editor.move) {
            stores.editor.setMove(false);
        }
        
        if (type === 'Magnifier') stores.editor.createSnap('init');
    }
    
    const handleSelectEmoji = (emoji) => {
        if (!stores.editor.isEditing) return;
        const x = stores.option.frameConf.width / 2 - 24;
        const y = stores.option.frameConf.height / 2 - 24;
        stores.editor.setUseTool(null);
        
        // 关闭移动模式
        if (stores.editor.move) {
            stores.editor.setMove(false);
        }
        
        stores.editor.addShape({
            id: nanoid(),
            type: 'emoji',
            text: emoji,
            zIndex: stores.editor.shapes.size + 1,
            x,
            y,
            editable: true
        });
    }
    
    // 使用工具函数重写切换移动画布功能
    const toggleMove = () => {
        if (!stores.editor.isEditing) {
            console.log("编辑器未处于编辑状态，无法切换移动模式");
            return;
        }
        
        try {
            // 切换移动模式状态
            const newMoveState = !stores.editor.move;
            console.log("手动切换移动模式:", newMoveState);
            
            // 关闭任何使用中的工具
            stores.editor.setUseTool(null);
            
            // 更新editor store状态
            stores.editor.setMove(newMoveState);
            
            // 获取Leafer应用实例
            const app = stores.editor.app;
            if (!app) {
                console.error("找不到app实例");
                return;
            }
            
            // 使用新的工具函数控制移动模式
            toggleCanvasMove(app, newMoveState);
            
        } catch (error) {
            console.error("切换移动模式出错:", error);
            stores.editor.setMove(false); // 出错时重置状态
        }
    };
    
    // 生成工具按钮
    const renderToolButton = (tool) => {
        let icon;
        
        if (tool.key === 'emoji') {
            return (
                <div key={tool.key} className="w-full">
                    <EmojiSelect 
                        disabled={false} 
                        theme={stores.editor.isDark ? 'dark' : 'light'} 
                        toSelect={handleSelectEmoji}
                        customButton={
                            <Button
                                type="text"
                                className="w-full flex items-center justify-start rounded-md px-2 py-1 h-auto hover:bg-purple-50 dark:hover:bg-gray-800 hover:text-purple-600 dark:hover:text-purple-400"
                            >
                                <Icon.Smile size={18} className="mr-2" />
                                <span className="text-xs">{tool.name}</span>
                            </Button>
                        }
                    />
                </div>
            );
        }
        
        if (tool.key === 'sticker' && tool.component) {
            return <tool.component key={tool.key} />;
        }
        
        if (tool.key === 'move') {
            return (
                <Tooltip 
                    title="按空格键可快速切换移动模式"
                    placement="right"
                >
                    <Button
                        key={tool.key}
                        type="text"
                        className={`w-full flex items-center justify-start rounded-md px-2 py-1 h-auto ${stores.editor.move ? 'bg-purple-100/50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400' : ''} hover:bg-purple-50 dark:hover:bg-gray-800 hover:text-purple-600 dark:hover:text-purple-400`}
                        onClick={toggleMove}
                    >
                        <Icon.Hand size={18} className="mr-2" />
                        <span className="text-xs">{tool.name}</span>
                        <span className="ml-1 text-xs text-gray-400">[空格]</span>
                    </Button>
                </Tooltip>
            );
        }
        
        if (tool.key === 'text' && tool.component) {
            return <tool.component key={tool.key} />;
        }
        
        if (tool.key.includes('Fill')) {
            icon = <Icon.Square size={18} className="mr-2" fill="currentColor" />;
        } else if (tool.key === 'Magnifier') {
            icon = <Icon.MessageCirclePlus size={18} className="mr-2" />;
        } else if (tool.key === 'Step') {
            icon = (
                <div className="border text-xs border-current w-4 h-4 mr-2 rounded-full text-center leading-4">
                    {stores.editor.nextStep}
                </div>
            );
        } else {
            const IconComponent = Icon[tool.key];
            icon = <IconComponent size={18} className="mr-2" />;
        }
        
        return (
            <Button
                key={tool.key}
                type="text"
                className={`w-full flex items-center justify-start rounded-md px-2 py-1 h-auto ${stores.editor.useTool === tool.key ? 'bg-purple-100/50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400' : ''} hover:bg-purple-50 dark:hover:bg-gray-800 hover:text-purple-600 dark:hover:text-purple-400`}
                onClick={() => selectTool(tool.key)}
            >
                {icon}
                <span className="text-xs">{tool.name}</span>
            </Button>
        );
    };

    return (
        <div className="bg-white dark:bg-gray-900 flex flex-col w-[180px] border-r border-gray-200 dark:border-gray-700 dark:text-gray-300 shadow-sm relative z-10 select-none">
            <div className="flex-1 flex flex-col gap-4 p-3 overflow-y-auto overflow-x-hidden">
                {toolGroups.map((group, index) => (
                    <div key={index} className="w-full">
                        <div className="flex items-center mb-2 pl-2 px-2 py-1 bg-purple-50 dark:bg-gray-800 rounded-md">
                            {group.icon}
                            <span className="text-sm font-medium ml-1 text-gray-700 dark:text-gray-300">{group.title}</span>
                        </div>
                        {group.custom === 'style' ? (
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center justify-between px-3 py-2 bg-white dark:bg-gray-900 rounded-md shadow-sm mb-2">
                                    <span className="text-xs">颜色:</span>
                                    <ColorPicker
                                        size='small'
                                        presets={[
                                            {
                                                label: '推荐颜色',
                                                colors: [
                                                    '#ffffff',
                                                    '#444444',
                                                    '#df4b26',
                                                    '#1677ff',
                                                    '#52C41A',
                                                    '#FA8C16',
                                                    '#FADB14',
                                                    '#EB2F96',
                                                    '#722ED1',
                                                ],
                                            },
                                        ]}
                                        value={stores.editor.annotateColor}
                                        onChange={(e) => stores.editor.setAnnotateColor(e.toHexString())}
                                    />
                                </div>
                                
                                <div className="flex items-center justify-between px-3 py-2 bg-white dark:bg-gray-900 rounded-md shadow-sm">
                                    <span className="text-xs">线宽:</span>
                                    <div className="flex items-center gap-2">
                                        <Slider
                                            className="w-20"
                                            min={1}
                                            max={10}
                                            step={1}
                                            marks={{ 1: '', 4: '', 6: '', 8: '', 10: '' }}
                                            value={Number(stores.editor.strokeWidth)}
                                            onChange={(value) => stores.editor.setStrokeWidth(value)}
                                            tooltip={{
                                                formatter: (value) => `${value}px`
                                            }}
                                        />
                                        <div className="w-4 h-4 flex items-center cursor-pointer">
                                            <i
                                                className="block w-full origin-center rounded-lg bg-slate-600 dark:bg-slate-400 -rotate-45"
                                                style={{
                                                    height: stores.editor.strokeWidth + 'px',
                                                }}
                                            ></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1">
                                {group.tools?.map(tool => renderToolButton(tool))}
                            </div>
                        )}
                    </div>
                ))}
                
                <Divider style={{ margin: '8px 0' }} />
            </div>
        </div>
    );
}); 